import React, { useEffect, useState } from "react";
import ThemeToggle from '../ThemeToggle';
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import AuthService from "services/auth";
import { AdminVendorApplicationsService } from "services/adminConsole";
import { useAuthUser } from "services/authSync";
import { useAdminIdleLogout } from "services/adminIdle";
import { BRAND, BRAND_LOGO as logoNav } from 'config/brand';
import { t } from "i18n";

const ADMIN_LINKS = [
  { to: "/admin", label: "Dashboard", end: true },
  { to: "/admin/products", label: "Products" },
  { to: "/admin/orders", label: "Orders" },
  { to: "/admin/users", label: "Users" },
  { to: "/admin/vendor-applications", label: "Vendor Applications" },
  { to: "/admin/stores", label: "Stores" },
  { to: "/admin/plans", label: "Plans" },
  { to: "/admin/billing", label: "Billing" },
  { to: "/admin/earnings", label: "Earnings" },
  { to: "/admin/commission", label: "Commission" },
  { to: "/admin/moderation", label: "Moderation" },
  { to: "/admin/taxonomy", label: "Taxonomy" },
  { to: "/admin/audit-log", label: "Audit Log" },
  { to: "/admin/subscriptions", label: "Subscriptions" },
  { to: "/admin/reviews", label: "Reviews" },
  { to: "/admin/review-queue", label: t("adminReviews.nav") },
  { to: "/admin/loyalty", label: t("adminLoyalty.nav") },
  { to: "/admin/deals", label: t("dealsAdmin.nav") },
  { to: "/admin/newsletter", label: "Newsletter" },
];

const navLinkClass = ({ isActive }) =>
  [
    "rounded-lg px-3 py-2 text-sm font-semibold transition",
    isActive
      ? "bg-slate-900 text-white"
      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
  ].join(" ");

export default function AdminShell() {
  const navigate = useNavigate();
  const user = useAuthUser(); // live across tabs (R0)
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const [pendingApplications, setPendingApplications] = useState(0);
  const isSuperuser = Boolean(user?.is_superuser);
  useAdminIdleLogout(isSuperuser); // R1C: 60 min of no activity signs out every admin tab

  // Pending vendor applications badge (R1B): refreshed on navigation and focus.
  useEffect(() => {
    if (!isSuperuser) return undefined;
    let cancelled = false;
    const loadPending = async () => {
      const result = await AdminVendorApplicationsService.list({ status: "PENDING", limit: 1 });
      if (!cancelled && result.success) setPendingApplications(result.total || 0);
    };
    loadPending();
    window.addEventListener("focus", loadPending);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", loadPending);
    };
  }, [isSuperuser, location.pathname]);

  const handleSignOut = () => {
    AuthService.logout("/admin-login");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white shadow-sm">
      <div className="mx-auto max-w-[1400px] px-4 py-3 sm:px-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <Link to="/admin" className="inline-flex shrink-0 items-center">
              <img
                src={logoNav}
                alt={`${BRAND.name} Admin`}
                className="h-9 w-auto object-contain"
                width={160}
                height={36}
                decoding="async"
              />
            </Link>
            <span className="hidden rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-900 sm:inline">
              Admin
            </span>
          </div>

          <div className="flex items-center gap-3 text-sm">
            {user ? (
              <span
                data-testid="admin-signed-in-as"
                title={user.email}
                className="hidden max-w-[260px] truncate font-medium text-slate-700 sm:inline"
              >
                Signed in as {user.email || user.display_name || user.username}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => navigate("/")}
              className="hidden text-xs font-semibold text-slate-600 underline hover:text-slate-900 sm:inline"
            >
              View store
            </button>
            <ThemeToggle className="h-8 w-8" />
            <button
              type="button"
              onClick={handleSignOut}
              className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-800 transition hover:bg-slate-200"
            >
              Sign out
            </button>
            <button
              type="button"
              className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 lg:hidden"
              onClick={() => setMenuOpen((o) => !o)}
              aria-expanded={menuOpen}
              aria-controls="admin-nav-menu"
            >
              Menu
            </button>
          </div>
        </div>

        <nav
          id="admin-nav-menu"
          className={[
            "mt-3 flex flex-wrap gap-1 border-t border-slate-100 pt-3",
            menuOpen ? "flex" : "hidden lg:flex",
          ].join(" ")}
          aria-label="Admin navigation"
        >
          {ADMIN_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={navLinkClass}
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
              {link.to === "/admin/vendor-applications" && pendingApplications > 0 ? (
                <span
                  data-testid="pending-applications-badge"
                  className="ml-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-bold text-on-accent"
                >
                  {pendingApplications}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
      </div>
    </header>
  );
}
